import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AccountDashboard.css';
import CardComponent from '../../components/cards/CardComponent';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { baseURL } from '../../index';
import { getHeaders } from '../../utility/getHeader';
import { 
  FaChartLine,
  FaMoneyBillWave,
  FaCreditCard,
  FaUsers,
  FaPercentage,
  FaCalendarAlt,
  FaSpinner,
  FaEye,
  FaDownload,
  FaChartPie,
  FaChartBar,
  FaCashRegister
} from 'react-icons/fa';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AccountDashboard = () => {
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState({
    summaryCards: {
      totalSales: 0,
      pendingBills: 0,
      paidBills: 0,
      totalDiscount: 0
    },
    cashierSummaries: [],
    salesByDate: [],
    weeklyData: [],
    salesSummary: null
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch data from APIs
  const fetchDashboardData = async () => {
    setLoading(true);
    console.log('Starting to fetch dashboard data...');
    
    try {
      const headers = getHeaders();
      console.log('Headers:', headers);
      console.log('Making API calls to fetch dashboard data...');

      // Fetch all data in parallel
      const [summaryResponse, paidSummaryResponse, pendingBillsResponse, adminSummaryRes] = await Promise.all([
        axios.get('/account/dashboard-summary', headers),
        axios.get('/admin/dashboard/paid-summary', headers),
        axios.get('/admin/dashboard/pending-bills-summary', headers),
        axios.get('/admin/dashboard/summary', headers)
      ]);
      
      console.log('Summary API Response:', summaryResponse.data);
      console.log('Paid Summary Response:', paidSummaryResponse.data);
      console.log('Pending Bills Response:', pendingBillsResponse.data);
      console.log('Admin Summary Response:', adminSummaryRes.data);
      
      if (summaryResponse.data.success && summaryResponse.data.data) {
        const apiData = summaryResponse.data.data;
        console.log('Dashboard summary data loaded:', apiData);
        
        // Get paid and pending amounts from dedicated APIs
        const paidAmount = paidSummaryResponse.data.success ? 
          ((paidSummaryResponse.data.summary?.total_net_amount || 0) - (paidSummaryResponse.data.summary?.total_discount || 0)) : 0;
        
        const pendingAmount = pendingBillsResponse.data.success ? 
          (pendingBillsResponse.data.summary?.total_net_amount || 0) : 0;
        
        // Get total sales and discount from admin summary API (same as dashboard.jsx)
        const totalSales = (adminSummaryRes.data.success && adminSummaryRes.data.data?.sales) ? 
          (adminSummaryRes.data.data.sales.total_sale || 0) : 0;
        
        const totalDiscount = (adminSummaryRes.data.success && adminSummaryRes.data.data?.discounts) ? 
          (adminSummaryRes.data.data.discounts.total_discount || 0) : 0;
        
        console.log('Total Sales from admin API:', totalSales);
        console.log('Total Discount from admin API:', totalDiscount);
        
        // Map API data to frontend structure
        const summaryCards = {
          totalSales: totalSales,
          pendingBills: pendingAmount,
          paidBills: paidAmount,
          totalDiscount: totalDiscount
        };
        
        // Use actual cashier data from API only, no mock data
        const cashierSummaries = apiData.cashier_summaries || apiData.cashierSummaries || [];
        
        // Create sales by date data for charts
        const salesByDate = [{
          date: new Date().toISOString().split('T')[0],
          bar_sales: apiData.final_bill_summary?.category_breakdown?.bar_amount || 0,
          food_sales: apiData.final_bill_summary?.category_breakdown?.food_amount || 0,
          shisha_sales: apiData.final_bill_summary?.category_breakdown?.shisha_amount || 0
        }];
        
        const newDashboardData = {
          summaryCards,
          cashierSummaries,
          salesByDate,
          weeklyData: salesByDate,
          salesSummary: apiData,
          financeRecords: apiData.finance_records,
          expensesSummary: apiData.expenses_summary,
          profitLoss: apiData.profit_loss
        };
        
        console.log('Setting dashboard data:', newDashboardData);
        setDashboardData(newDashboardData);
        
        console.log('Dashboard data set successfully');
      } else {
        console.warn('Invalid API response structure:', summaryResponse.data);
        setError('Invalid data received from server');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      
      let errorMessage = 'Failed to load dashboard data.';
      
      if (error.response?.status === 500) {
        errorMessage = `Server Error (500): ${error.response?.data?.message || 'Internal server error occurred'}`;
        console.error('Server error details:', error.response?.data);
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view this data.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <Header title="Account Dashboard" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <FaSpinner className="fa-spin" size={50} color="#667eea" />
            <p className="mt-3">Loading dashboard data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Header title="Account Dashboard" />
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn-outline-danger btn-sm ms-3" 
              onClick={() => {
                setError('');
                fetchDashboardData();
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Summary Cards Data
  const summaryCardsData = [
    {
      title: 'Total Sales',
      value: `฿${dashboardData.summaryCards.totalSales.toLocaleString()}`,
      icon: <FaMoneyBillWave />,
      gradient: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
      textColor: '#2e7d32',
      iconColor: '#1976d2'
    },
    {
      title: 'Pending Bills',
      value: `฿${dashboardData.summaryCards.pendingBills.toLocaleString()}`,
      icon: <FaCreditCard />,
      gradient: 'linear-gradient(135deg, #fce4ec 0%, #f8f9fa 100%)',
      textColor: '#c2185b',
      iconColor: '#e91e63'
    },
    {
      title: 'Paid Bills',
      value: `฿${dashboardData.summaryCards.paidBills.toLocaleString()}`,
      icon: <FaCashRegister />,
      gradient: 'linear-gradient(135deg, #e8f5e8 0%, #f8f9fa 100%)',
      textColor: '#2e7d32',
      iconColor: '#4caf50'
    },
    {
      title: 'Total Discount',
      value: `฿${dashboardData.summaryCards.totalDiscount.toLocaleString()}`,
      icon: <FaPercentage />,
      gradient: 'linear-gradient(135deg, #fff3e0 0%, #f8f9fa 100%)',
      textColor: '#f57c00',
      iconColor: '#ff9800'
    }
  ];

  // Pie Chart Data for Sales Distribution
  const pieChartData = {
    labels: ['Paid Bills', 'Pending Bills'],
    datasets: [{
      data: [
        dashboardData.summaryCards.paidBills,
        dashboardData.summaryCards.pendingBills
      ],
      backgroundColor: [
        '#81c784', // Soft green for paid
        '#f48fb1'  // Soft pink for pending
      ],
      borderColor: [
        '#66bb6a',
        '#ec407a'
      ],
      borderWidth: 2
    }]
  };

  // Bar Chart Data for Bar vs Food vs Shisha Sales
  const calculateSalesData = () => {
    // Use data from the new API structure
    if (dashboardData.salesSummary?.final_bill_summary?.category_breakdown) {
      const categoryData = dashboardData.salesSummary.final_bill_summary.category_breakdown;
      
      return {
        labels: ['Today\'s Sales'],
        barSales: [categoryData.bar_amount || 0],
        foodSales: [categoryData.food_amount || 0],
        shishaSales: [categoryData.shisha_amount || 0]
      };
    }
    
    // Fallback if no data
    return {
      labels: [],
      barSales: [],
      foodSales: [],
      shishaSales: []
    };
  };

  const { labels, barSales, foodSales, shishaSales } = calculateSalesData();

  const barChartData = {
    labels,
    datasets: [
      {
        label: 'Bar Sales',
        data: barSales,
        backgroundColor: '#81c784',
        borderColor: '#66bb6a',
        borderWidth: 2
      },
      {
        label: 'Food Sales',
        data: foodSales,
        backgroundColor: '#f48fb1',
        borderColor: '#ec407a',
        borderWidth: 2
      },
      {
        label: 'Shisha Sales',
        data: shishaSales,
        backgroundColor: '#64b5f6', 
        borderColor: '#42a5f5',
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Sales Breakdown by Category'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '฿' + value.toLocaleString();
          }
        }
      }
    }
  };

  return (
    <Layout>
      <Header title="Account Dashboard" />
      <div className="analytics-dashboard">
        {/* Section 1: Summary Cards */}
        <div className="row mb-4">
          {summaryCardsData.map((card, index) => (
            <div key={index} className="col-lg-3 col-md-6 col-sm-6 mb-3">
              <div 
                className="metric-card-analytics" 
                style={{
                  background: card.gradient,
                  borderRadius: '12px',
                  padding: '20px',
                  color: card.textColor,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '120px',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
                }}
              >
                <div className="d-flex justify-content-between align-items-center h-100">
                  <div className="metric-header">
                    <span className="metric-value" style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                      {card.value}
                    </span>
                    <span className="metric-label" style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                      {card.title}
                    </span>
                  </div>
                  <div style={{ fontSize: '2rem', opacity: 0.8, color: card.iconColor }}>
                    {card.icon}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section 2: Cashier Summaries */}
        <div className="row mb-4">
          <div className="col-12">
            <CardComponent>
              <h5 className="mb-3" style={{ color: '#667eea', fontWeight: '600' }}>
                👥 Cashier Performance Summary
              </h5>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead style={{ backgroundColor: 'white' }}>
                    <tr>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Cashier ID</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Name</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Date</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Total Sales</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Transactions</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Cash Amount</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Card Amount</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Transfer Amount</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.cashierSummaries.slice(0, 10).map((cashier, index) => (
                      <tr key={index}>
                        <td><strong>{cashier.cashier_id}</strong></td>
                        <td>{cashier.cashier_name}</td>
                        <td>{cashier.date ? new Date(cashier.date).toLocaleDateString() : 'N/A'}</td>
                        <td>฿{(cashier.total_sales || 0).toLocaleString()}</td>
                        <td>{cashier.total_transactions || 0}</td>
                        <td>฿{(cashier.cash_amount || 0).toLocaleString()}</td>
                        <td>฿{(cashier.card_amount || 0).toLocaleString()}</td>
                        <td>฿{(cashier.transfer_amount || 0).toLocaleString()}</td>
                        <td>
                          <span className={`badge ${cashier.counter_status === 'closed' ? 'bg-success' : 'bg-warning'}`}>
                            {cashier.counter_status === 'closed' ? 'Closed' : 'Open'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {dashboardData.cashierSummaries.length > 0 && (
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', borderTop: '2px solid #667eea' }}>
                        <td colSpan="3" style={{ textAlign: 'right', paddingRight: '20px' }}>TOTAL:</td>
                        <td>฿{dashboardData.cashierSummaries.reduce((sum, cashier) => sum + (cashier.total_sales || 0), 0).toLocaleString()}</td>
                        <td>{dashboardData.cashierSummaries.reduce((sum, cashier) => sum + (cashier.total_transactions || 0), 0)}</td>
                        <td>฿{dashboardData.cashierSummaries.reduce((sum, cashier) => sum + (cashier.cash_amount || 0), 0).toLocaleString()}</td>
                        <td>฿{dashboardData.cashierSummaries.reduce((sum, cashier) => sum + (cashier.card_amount || 0), 0).toLocaleString()}</td>
                        <td>฿{dashboardData.cashierSummaries.reduce((sum, cashier) => sum + (cashier.transfer_amount || 0), 0).toLocaleString()}</td>
                        <td>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {dashboardData.cashierSummaries.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">No cashier data available</p>
                  </div>
                )}
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Section 3: Charts */}
        <div className="row mb-4">
          <div className="col-md-6">
            <CardComponent>
              <h5 className="mb-3" style={{ color: '#667eea', fontWeight: '600' }}>
                <FaChartPie className="me-2" />
                Sales Distribution
              </h5>
              <div style={{ height: '350px' }}>
                <Pie 
                  data={pieChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          font: {
                            size: 12,
                            weight: 'bold'
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const label = context.label || '';
                            const value = context.parsed;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${label}: ฿${value.toLocaleString()} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </CardComponent>
          </div>
          <div className="col-md-6">
            <CardComponent style={{ minHeight: '320px' }}>
              <h5 className="mb-3" style={{ color: '#667eea', fontWeight: '600' }}>
                🍽️ Category Breakdown Summary
              </h5>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ color: 'white', backgroundColor: '#667eea', borderRadius: '5px 0 0 0' }}>Category</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea' }}>Description</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea', textAlign: 'center' }}>Total Orders</th>
                      <th style={{ color: 'white', backgroundColor: '#667eea', borderRadius: '0 5px 0 0', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🍺</span>
                          <strong style={{ color: '#2e7d32' }}>Bar Sales</strong>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">Beverages & Drinks</small>
                      </td>
                      <td className="text-center">
                        <strong style={{ color: '#2e7d32', fontSize: '1rem' }}>
                          {(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.bar_orders || 0).toLocaleString()}
                        </strong>
                      </td>
                      <td className="text-right">
                        <strong style={{ color: '#2e7d32', fontSize: '1.1rem' }}>
                          ฿{(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.bar_amount || 0).toLocaleString()}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🍽️</span>
                          <strong style={{ color: '#c2185b' }}>Food Sales</strong>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">Meals & Cuisine</small>
                      </td>
                      <td className="text-center">
                        <strong style={{ color: '#c2185b', fontSize: '1rem' }}>
                          {(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.food_orders || 0).toLocaleString()}
                        </strong>
                      </td>
                      <td className="text-right">
                        <strong style={{ color: '#c2185b', fontSize: '1.1rem' }}>
                          ฿{(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.food_amount || 0).toLocaleString()}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>💨</span>
                          <strong style={{ color: '#1976d2' }}>Shisha Sales</strong>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">Hookah & Flavors</small>
                      </td>
                      <td className="text-center">
                        <strong style={{ color: '#1976d2', fontSize: '1rem' }}>
                          {(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.shisha_orders || 0).toLocaleString()}
                        </strong>
                      </td>
                      <td className="text-right">
                        <strong style={{ color: '#1976d2', fontSize: '1.1rem' }}>
                          ฿{(dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.shisha_amount || 0).toLocaleString()}
                        </strong>
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #667eea' }}>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>📊</span>
                          <strong style={{ color: '#667eea', fontSize: '1.1rem' }}>Total Sales</strong>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">Combined Revenue</small>
                      </td>
                      <td className="text-center">
                        <strong style={{ color: '#667eea', fontSize: '1.1rem' }}>
                          {((dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.bar_orders || 0) + 
                            (dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.food_orders || 0) + 
                            (dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.shisha_orders || 0)).toLocaleString()}
                        </strong>
                      </td>
                      <td className="text-right">
                        <strong style={{ color: '#667eea', fontSize: '1.3rem' }}>
                          ฿{((dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.bar_amount || 0) + 
                             (dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.food_amount || 0) + 
                             (dashboardData.salesSummary?.final_bill_summary?.category_breakdown?.shisha_amount || 0)).toLocaleString()}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Section 4: Sales by Category Chart (Bar/Food/Shisha) */}
        <div className="row">
          <div className="col-12">
            <CardComponent>
              <h5 className="mb-3" style={{ color: '#667eea', fontWeight: '600' }}>
                <FaChartBar className="me-2" />
                Sales by Category (Bar/Food/Shisha)
              </h5>
              <div style={{ height: '400px' }}>
                <Bar data={barChartData} options={chartOptions} />
              </div>
            </CardComponent>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountDashboard;